import React from 'react';
import PolicyLayout from './PolicyLayout';
import useDocumentMeta from '../hooks/useDocumentMeta';

const LAST_UPDATED = '2026-06-02';

const AccessibilityStatement = () => {
  useDocumentMeta({
    title: 'Accessibility Statement — Factory OS',
    description:
      'Factory OS aims to align with WCAG 2.1 AA. This page describes our accessibility commitments, what we have done, known limitations, and how to report a barrier.',
    path: '/accessibility',
  });

  return (
    <PolicyLayout
      title="Accessibility Statement"
      lastUpdated={LAST_UPDATED}
      lead="Engineering tools should be usable by every engineer. We aim to make Factory OS work well with keyboards, screen readers, magnification, and other assistive technologies."
    >
      <h2>1. Our commitment</h2>
      <p>
        Factory OS is committed to making our website and platform accessible to as many people as possible, including people who use assistive technology. We treat accessibility as part of the product, not an afterthought.
      </p>

      <h2>2. Standard we aim for</h2>
      <p>
        We aim to align with the <a href="https://www.w3.org/TR/WCAG21/" rel="noopener noreferrer">Web Content Accessibility Guidelines (WCAG) 2.1 at Level AA</a>. We are not formally certified against these guidelines, and some areas may not yet meet them — see &quot;Known limitations&quot; below.
      </p>

      <h2>3. What we have done</h2>
      <ul>
        <li>
          <strong>Semantic structure.</strong> We use semantic HTML (headings, lists, landmarks, buttons, links) so screen readers can navigate the page in a predictable order.
        </li>
        <li>
          <strong>Keyboard navigation.</strong> Primary interactive elements — links, buttons, menus, and form fields — are reachable and operable from a keyboard.
        </li>
        <li>
          <strong>Visible focus.</strong> Interactive elements show a visible focus indicator when navigated by keyboard.
        </li>
        <li>
          <strong>Color contrast.</strong> Body text on the marketing pages targets a contrast ratio of at least 4.5:1 against its background, and large text targets at least 3:1.
        </li>
        <li>
          <strong>Alt text on meaningful images.</strong> Decorative SVG motifs are marked with <code>aria-hidden=&quot;true&quot;</code> so screen readers skip them; meaningful images use descriptive <code>alt</code> attributes.
        </li>
        <li>
          <strong>Form labels and error states.</strong> Form fields are paired with visible labels. Required fields are indicated. Validation errors are presented in text, not by color alone.
        </li>
        <li>
          <strong>Reduced motion.</strong> Pages that include animation honor the <code>prefers-reduced-motion</code> setting — scroll-snap is relaxed and background animations pause when reduced motion is requested.
        </li>
        <li>
          <strong>Responsive layout.</strong> Layouts reflow at narrow widths and remain usable at 400% zoom on desktop.
        </li>
      </ul>

      <h2>4. Known limitations</h2>
      <p>
        We know some areas do not yet meet our goal. We are working on them, and we appreciate reports of any barrier you encounter.
      </p>
      <ul>
        <li>
          <strong>Decorative animations.</strong> The cinematic landing page includes background animations. Users who request reduced motion will see them paused, but some motion may still play.
        </li>
        <li>
          <strong>Embedded technical content.</strong> Charts and diagrams inside the platform may not yet have full text equivalents.
        </li>
        <li>
          <strong>Third-party content.</strong> Vendor tools (e.g., payment forms, video embeds) may not perfectly match our accessibility standards. We pick vendors with accessibility in mind and report issues to them.
        </li>
        <li>
          <strong>Active development.</strong> Factory OS is under active development. New features may temporarily ship with accessibility gaps; we treat those as bugs.
        </li>
      </ul>

      <h2>5. Compatibility</h2>
      <p>
        We test on current versions of major browsers (Chrome, Firefox, Safari, Edge) on Windows, macOS, iOS, and Android. We aim to support recent versions of common screen readers, including NVDA, JAWS, VoiceOver, and TalkBack. If you use an older browser or assistive technology and run into problems, please tell us — we may be able to help.
      </p>

      <h2>6. Report an accessibility barrier</h2>
      <p>
        If something is not working for you — a control that is not reachable from a keyboard, text that is hard to read, an error you cannot dismiss, or any other barrier — please tell us. We treat accessibility reports as priority bugs.
      </p>
      <p>
        Contact us through the <a href="/contact">contact page</a> and mention &quot;accessibility&quot; in your message. Where possible, include:
      </p>
      <ul>
        <li>The URL of the page or feature</li>
        <li>What you were trying to do</li>
        <li>What happened (and what you expected)</li>
        <li>Your browser, operating system, and assistive technology (if any)</li>
      </ul>

      <h2>7. Formal complaints</h2>
      <p>
        If you have raised an accessibility concern with us and are not satisfied with our response, you may be able to file a complaint with the accessibility regulator in your jurisdiction. The exact body and procedure vary by country.
      </p>

      <h2>8. Updates</h2>
      <p>
        This statement will be reviewed and updated as we improve accessibility and as our product evolves. The &quot;Last updated&quot; date at the top of this page reflects the most recent revision.
      </p>
    </PolicyLayout>
  );
};

export default AccessibilityStatement;
